// The additive, immutable render-input context (design D9 — the cross-lane contract the
// `replay-viewer` lane consumes). Seeded here as group-1 test infrastructure so every
// downstream group's tests (mode, anchor, facing, season) assert against one shared
// shape; group 3.2 produces a live value from additive store selectors and threads it
// through the render entry point (canvas internals read this context, never the global
// store).
//
// It carries the signals that arrive INDEPENDENTLY of the world snapshot: the streamed
// `season`, the own-hive world anchor (or null for spectator / pre-handshake), the
// own-colony lifecycle, a replay `playhead` slot, and two monotonic discontinuity
// counters. `connectionEpoch` (the Connection's reconnect epoch) drives the
// facing-tombstone reset — facing holds across a bare sequence gap. `streamEpoch` (an
// explicit counter bumped once per reconnect AND once per sequence-gap entry) drives the
// per-entity sample-history reset. No transient `GapWait`/connection-state field rides
// this context — only the two monotonic counters (design D9/F10).

import { Season } from '../net/wire/messages';

/** The own colony's play state, sourced from `MatchState.eliminations` (group 3). The
 * hive + queen anchor renders only while `live`; `eliminated` clears the whole anchor. */
export type ColonyLifecycle = 'live' | 'eliminated';

export interface RenderInput {
  readonly season: Season;
  readonly ownHiveAnchor: readonly [number, number] | null;
  readonly ownColonyLifecycle: ColonyLifecycle;
  /** The viewer's own colony id, or `null` for a spectator / before the `Welcome` lands
   * (bee-body-palette). The close tier colours each bee by its ROLE relative to this — own
   * bees by their type colour, enemies by their colony colour with a red band — so the id
   * has to reach the canvas; like the anchor and the lifecycle it arrives on the
   * `Welcome`/lobby claim, independently of world deltas, which is why it rides here rather
   * than on the world snapshot. */
  readonly ownColonyId: number | null;
  /** The replay playhead tick, or `null` in live play (no playhead). A backward move
   * resets interpolation history without advancing decorative hover-bob (design D8). */
  readonly playhead: number | null;
  /** The Connection's reconnect epoch — bumped on reconnect only. Drives the
   * facing-tombstone reset. */
  readonly connectionEpoch: number;
  /** An explicit monotonic counter bumped once per reconnect and once per sequence-gap
   * (`GapWait`) entry — never decreasing, never bumped on recovery. Drives the
   * per-entity sample-history reset. */
  readonly streamEpoch: number;
  /**
   * Each bee's resolved B++ state name, keyed by bee id — the **rounds-only** channel the
   * state-label layer reads (WAG-36, design **E5**). Supplied by the rounds world adapter from
   * the trace's preamble state-name table; a bee whose state is the trace's no-state sentinel
   * (a bot colony's bee, a queen) simply has no entry.
   *
   * **Absent — never empty-but-present — on the live path**, so a live frame stays
   * distinguishable from a rounds frame with no labelled bees. It rides here rather than on the
   * live wire's `BeeRecord`, which is consumed by the live codec's golden-bytes tests, the
   * colony screen's inspect model, picking, flower attachment and the ambient world: a
   * rounds-only field there would either drift the client's mirror from `waggle-proto`'s codec
   * or leave every live path carrying a permanently-`undefined` field.
   */
  readonly beeStateNames?: ReadonlyMap<number, string>;
  /**
   * The tapped bee's id, drawn with a blue outline silhouette, or `null`/absent when nothing is
   * selected.
   *
   * Rides here rather than as another `buildDrawables` parameter (which already takes seven) for
   * the same reason the anchor and the own-colony id do: it arrives independently of world deltas,
   * changing on a tap rather than on a frame. Every screen that picks — the colony screen, the
   * sandbox, the graded review and the demo stage — already builds a render input, so this costs
   * them one field each and costs the renderer no new plumbing.
   */
  readonly selectedBeeId?: number | null;
}

/** The backward-compatible default: a live-play context with a null anchor and no
 * playhead, so an existing call site that passes only world/camera/tier still renders
 * (spec: "existing render call sites keep working without the new inputs"). Frozen —
 * the render-input context is immutable. */
export const LIVE_PLAY_RENDER_INPUT: RenderInput = Object.freeze({
  season: Season.Summer,
  ownHiveAnchor: null,
  ownColonyLifecycle: 'live',
  ownColonyId: null,
  playhead: null,
  connectionEpoch: 0,
  streamEpoch: 0,
});

/** Value equality over a render-input context (implementation-review F13). `selectRenderInput`
 * builds a fresh object per store read (no memoization), so a call site cannot detect an
 * independent (non-world) render-input change by reference — it compares by value. Used by the
 * `InMatchCanvas`/`Match` host subscriptions to decide whether a store update that carried no
 * world delta nonetheless changed the render-input (season / anchor / lifecycle / epoch) and
 * so must be pushed to the renderer's live-input via `updateRenderInput`. The anchor is a
 * two-tuple-or-null compared component-wise. */
export function renderInputEquals(a: RenderInput, b: RenderInput): boolean {
  return (
    a.season === b.season &&
    a.ownColonyLifecycle === b.ownColonyLifecycle &&
    a.ownColonyId === b.ownColonyId &&
    a.playhead === b.playhead &&
    a.connectionEpoch === b.connectionEpoch &&
    a.streamEpoch === b.streamEpoch &&
    // `?? null` so an absent field and an explicit null compare equal — a screen that never
    // selects anything must not read as changed against one that has cleared its selection.
    (a.selectedBeeId ?? null) === (b.selectedBeeId ?? null) &&
    // Reference identity, deliberately: on the live path both are absent (equal), and the
    // rounds adapter builds a fresh map per frame, so a rounds frame is never mistaken for
    // an unchanged one. Comparing map CONTENTS here would be a per-bee walk on every store
    // read to answer a question the live path never asks.
    a.beeStateNames === b.beeStateNames &&
    anchorEquals(a.ownHiveAnchor, b.ownHiveAnchor)
  );
}

function anchorEquals(
  a: readonly [number, number] | null,
  b: readonly [number, number] | null,
): boolean {
  if (a === null || b === null) return a === b;
  return a[0] === b[0] && a[1] === b[1];
}
